import jsPDF from 'jspdf';
import KitchenInvoice from '../components/invoices/KitchenInvoice';
import CustomerInvoice from '../components/invoices/CustomerInvoice';
import ReactDOMServer from 'react-dom/server';

// Import autoTable plugin
let autoTable;
try {
  autoTable = require('jspdf-autotable').default;
} catch (e) {
  try {
    autoTable = require('jspdf-autotable');
  } catch (e2) {
    console.error('Failed to load jspdf-autotable:', e2);
    autoTable = null;
  }
}

class InvoiceService {
  /**
   * Generate Kitchen Invoice PDF
   * @param {Object} order - Order data
   * @param {Object} outlet - Outlet data
   * @returns {Promise<Blob>} PDF blob
   */
  async generateKitchenInvoicePDF(order, outlet) {
    try {
      // console.log('Generating kitchen invoice PDF for order:', order);
      // console.log('Outlet data:', outlet);
      
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Set font
      doc.setFont('helvetica');
      
      // Simple Header - Just Order Number and Type
      doc.setFontSize(20);
      doc.setTextColor(25, 118, 210);
      doc.text('KITCHEN ORDER', pageWidth / 2, 30, { align: 'center' });
      
      let yPosition = 50;
      
      // Order Number and Type Only
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text(`Order: ${order?.order_number || 'N/A'}`, 20, yPosition);
      yPosition += 8;
      
      doc.setFont('helvetica', 'bold');
      doc.text(`Type: ${this.getOrderTypeLabel(order?.order_type)}`, 20, yPosition);
      yPosition += 8;
      
      // Table Number (for dine-in orders only)
      if (order?.table) {
        doc.setFont('helvetica', 'bold');
        doc.text(`Table: ${order.table.table_number || order.table.name}`, 20, yPosition);
        yPosition += 8;
      }
      
      yPosition += 10;
      
      // Menu Items Table - This is the main focus
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text('MENU ITEMS TO PREPARE', 20, yPosition);
      yPosition += 15;
      
      const tableData = order?.order_items?.map(item => [
        item.menu_item?.name || item.menu_item_name || 'Unknown Item',
        item.quantity.toString(),
        item.special_instructions || '-'
      ]) || [];
      
      if (autoTable) {
        try {
          autoTable(doc, {
            startY: yPosition,
            head: [['Item Name', 'Quantity', 'Special Instructions']],
            body: tableData,
            styles: {
              fontSize: 12,
              cellPadding: 6,
            },
            headStyles: {
              fillColor: [25, 118, 210],
              textColor: 255,
              fontStyle: 'bold',
            },
            alternateRowStyles: {
              fillColor: [248, 249, 250],
            },
            margin: { left: 20, right: 20 },
          });
          yPosition = doc.lastAutoTable.finalY + 20;
        } catch (autoTableError) {
          console.error('AutoTable error:', autoTableError);
          // Fallback: create a simple table without autoTable
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text('MENU ITEMS:', 20, yPosition);
          yPosition += 10;
          doc.setFont('helvetica', 'normal');
          tableData.forEach((row, index) => {
            doc.text(`${index + 1}. ${row[0]} (Qty: ${row[1]})`, 25, yPosition);
            yPosition += 8;
            if (row[2] && row[2] !== '-') {
              doc.text(`   Special: ${row[2]}`, 25, yPosition);
              yPosition += 6;
            }
          });
          yPosition += 10;
        }
      } else {
        console.warn('AutoTable not available, using fallback table');
        // Fallback: create a simple table without autoTable
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('MENU ITEMS:', 20, yPosition);
        yPosition += 10;
        doc.setFont('helvetica', 'normal');
        tableData.forEach((row, index) => {
          doc.text(`${index + 1}. ${row[0]} (Qty: ${row[1]})`, 25, yPosition);
          yPosition += 8;
          if (row[2] && row[2] !== '-') {
            doc.text(`   Special: ${row[2]}`, 25, yPosition);
            yPosition += 6;
          }
        });
        yPosition += 10;
      }
      
      // Simple Footer
      yPosition = Math.max(yPosition, 250);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.text('Kitchen Order - Prepare with care!', pageWidth / 2, yPosition, { align: 'center' });
      
      return doc.output('blob');
    } catch (error) {
      console.error('Error generating kitchen invoice PDF:', error);
      throw error;
    }
  }

  /**
   * Generate Customer Invoice PDF
   * @param {Object} order - Order data
   * @param {Object} outlet - Outlet data
   * @returns {Promise<Blob>} PDF blob
   */
  async generateCustomerInvoicePDF(order, outlet) {
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Set font
      doc.setFont('helvetica');
      
      // Header
      doc.setFontSize(24);
      doc.setTextColor(25, 118, 210); // Primary blue
      doc.text('CUSTOMER INVOICE', pageWidth / 2, 30, { align: 'center' });
      
      doc.setFontSize(16);
      doc.setTextColor(100, 100, 100);
      doc.text('Order Receipt & Payment Confirmation', pageWidth / 2, 40, { align: 'center' });
      
      // Line separator
      doc.setDrawColor(200, 200, 200);
      doc.line(20, 50, pageWidth - 20, 50);
      
      let yPosition = 70;
      
      // Restaurant Information
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('RESTAURANT INFORMATION', 20, yPosition);
      yPosition += 10;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.text(outlet?.name || order?.outlet_name || 'Restaurant Name', 20, yPosition);
      yPosition += 6;
      doc.text(outlet?.address || 'Restaurant Address', 20, yPosition);
      yPosition += 6;
      doc.text(`Phone: ${outlet?.phone || '+65 1234 5678'}`, 20, yPosition);
      yPosition += 6;
      doc.text(`Email: ${outlet?.email || 'info@restaurant.com'}`, 20, yPosition);
      yPosition += 15;
      
      // Order Details
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('ORDER DETAILS', 20, yPosition);
      yPosition += 10;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.text(`Invoice #: ${order?.order_number}`, 20, yPosition);
      yPosition += 6;
      doc.text(`Date: ${new Date(order?.created_at).toLocaleDateString('en-SG')}`, 20, yPosition);
      yPosition += 6;
      doc.text(`Type: ${this.getOrderTypeLabel(order?.order_type)}`, 20, yPosition);
      yPosition += 6;
      if (order?.table) {
        doc.text(`Table: ${order.table.table_number || order.table.name}`, 20, yPosition);
        yPosition += 6;
      }
      yPosition += 15;
      
      // Customer Information
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('CUSTOMER INFORMATION', 20, yPosition);
      yPosition += 10;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.text(`Name: ${order?.customer_name || 'Guest Customer'}`, 20, yPosition);
      yPosition += 6;
      doc.text(`Phone: ${order?.customer_phone || 'N/A'}`, 20, yPosition);
      yPosition += 6;
      if (order?.delivery_address) {
        doc.text(`Delivery Address: ${order.delivery_address}`, 20, yPosition);
        yPosition += 6;
      }
      if (order?.special_instructions) {
        doc.text(`Special Instructions: ${order.special_instructions}`, 20, yPosition);
        yPosition += 6;
      }
      yPosition += 15;
      
      // Menu Items Table
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('ORDER ITEMS', 20, yPosition);
      yPosition += 15;
      
      const tableData = order?.order_items?.map(item => {
        const itemPrice = item.menu_item?.price || 0;
        const itemTotal = itemPrice * item.quantity;
        return [
          item.menu_item?.name || 'Unknown Item',
          item.quantity.toString(),
          `S$${itemPrice.toFixed(2)}`,
          `S$${itemTotal.toFixed(2)}`
        ];
      }) || [];
      
      if (autoTable) {
        try {
          autoTable(doc, {
            startY: yPosition,
            head: [['Item', 'Qty', 'Price', 'Total']],
            body: tableData,
            styles: {
              fontSize: 10,
              cellPadding: 4,
            },
            headStyles: {
              fillColor: [25, 118, 210],
              textColor: 255,
              fontStyle: 'bold',
            },
            alternateRowStyles: {
              fillColor: [248, 249, 250],
            },
            margin: { left: 20, right: 20 },
          });
          yPosition = doc.lastAutoTable.finalY + 20;
        } catch (autoTableError) {
          console.error('AutoTable error:', autoTableError);
          // Fallback: create a simple table without autoTable
          doc.setFontSize(10);
          doc.text('Order Items:', 20, yPosition);
          yPosition += 10;
          tableData.forEach((row, index) => {
            doc.text(`${index + 1}. ${row[0]} (Qty: ${row[1]}, Price: ${row[2]}, Total: ${row[3]})`, 25, yPosition);
            yPosition += 6;
          });
          yPosition += 10;
        }
      } else {
        console.warn('AutoTable not available, using fallback table');
        // Fallback: create a simple table without autoTable
        doc.setFontSize(10);
        doc.text('Order Items:', 20, yPosition);
        yPosition += 10;
        tableData.forEach((row, index) => {
          doc.text(`${index + 1}. ${row[0]} (Qty: ${row[1]}, Price: ${row[2]}, Total: ${row[3]})`, 25, yPosition);
          yPosition += 6;
        });
        yPosition += 10;
      }
      
      // Order Summary
      const subtotal = this.calculateSubtotal(order);
      const deliveryFee = this.calculateDeliveryFee(order);
      const tax = this.calculateTax(subtotal);
      const total = subtotal + deliveryFee + tax;
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('ORDER SUMMARY', 20, yPosition);
      yPosition += 15;
      
      // Summary box
      const summaryX = 20;
      const summaryY = yPosition;
      const summaryWidth = pageWidth - 40;
      const summaryHeight = 40;
      
      doc.setDrawColor(200, 200, 200);
      doc.rect(summaryX, summaryY, summaryWidth, summaryHeight);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      let summaryYPos = summaryY + 8;
      
      doc.text('Subtotal:', summaryX + 5, summaryYPos);
      doc.text(`S$${subtotal.toFixed(2)}`, summaryX + summaryWidth - 5, summaryYPos, { align: 'right' });
      summaryYPos += 6;
      
      if (deliveryFee > 0) {
        doc.text('Delivery Fee:', summaryX + 5, summaryYPos);
        doc.text(`S$${deliveryFee.toFixed(2)}`, summaryX + summaryWidth - 5, summaryYPos, { align: 'right' });
        summaryYPos += 6;
      }
      
      doc.text('GST (18%):', summaryX + 5, summaryYPos);
      doc.text(`S$${tax.toFixed(2)}`, summaryX + summaryWidth - 5, summaryYPos, { align: 'right' });
      summaryYPos += 8;
      
      // Total line
      doc.setDrawColor(0, 0, 0);
      doc.line(summaryX + 5, summaryYPos, summaryX + summaryWidth - 5, summaryYPos);
      summaryYPos += 8;
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('Total Amount:', summaryX + 5, summaryYPos);
      doc.setTextColor(25, 118, 210);
      doc.text(`S$${total.toFixed(2)}`, summaryX + summaryWidth - 5, summaryYPos, { align: 'right' });
      
      yPosition += summaryHeight + 20;
      
      // Payment Information
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('PAYMENT INFORMATION', 20, yPosition);
      yPosition += 15;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.text(`Payment Method: ${order?.payment_method === 'cash_on_delivery' ? 'Cash on Delivery' : 'Online Payment'}`, 20, yPosition);
      yPosition += 6;
      doc.text(`Payment Status: ${order?.payment_status?.toUpperCase() || 'PENDING'}`, 20, yPosition);
      yPosition += 20;
      
      // Footer
      doc.setDrawColor(200, 200, 200);
      doc.line(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Thank you for choosing our restaurant! We appreciate your business.', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 6;
      doc.text(`For any questions, please contact us at ${outlet?.phone || '+65 1234 5678'}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 6;
      doc.text(`Generated on ${new Date().toLocaleDateString('en-SG')} at ${new Date().toLocaleTimeString('en-SG')}`, pageWidth / 2, yPosition, { align: 'center' });
      
      return doc.output('blob');
    } catch (error) {
      console.error('Error generating customer invoice PDF:', error);
      throw error;
    }
  }

  /**
   * Download PDF file
   * @param {Blob} pdfBlob - PDF blob
   * @param {string} filename - Filename for download
   */
  downloadPDF(pdfBlob, filename) {
    try {
      // console.log('Downloading PDF:', filename);
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      // console.log('PDF download completed');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }

  /**
   * Get order type label
   * @param {string} orderType - Order type
   * @returns {string} Order type label
   */
  getOrderTypeLabel(orderType) {
    switch (orderType) {
      case 'delivery':
        return 'Delivery Order';
      case 'takeaway':
        return 'Takeaway Order';
      case 'DINE_IN':
      case 'table_order':
        return 'Dine-in Order';
      default:
        return 'Order';
    }
  }

  /**
   * Calculate subtotal
   * @param {Object} order - Order data
   * @returns {number} Subtotal
   */
  calculateSubtotal(order) {
    if (!order?.order_items) return 0;
    return order.order_items.reduce((total, item) => {
      return total + (item.menu_item?.price || 0) * item.quantity;
    }, 0);
  }

  /**
   * Calculate delivery fee
   * @param {Object} order - Order data
   * @returns {number} Delivery fee
   */
  calculateDeliveryFee(order) {
    if (order?.order_type === 'delivery') {
      const subtotal = this.calculateSubtotal(order);
      return subtotal > 500 ? 0 : 50; // Free delivery over $500
    }
    return 0;
  }

  /**
   * Calculate tax
   * @param {number} subtotal - Subtotal amount
   * @returns {number} Tax amount
   */
  calculateTax(subtotal) {
    return subtotal * 0.18; // 18% GST
  }
}

export default new InvoiceService();





